/* eslint-disable @typescript-eslint/camelcase */
import uuidv1 from 'uuid/v1';
import BrowserDetector from '@/scripts/helpers/browser-detector';

import webcall from '@/scripts/webcall';
import analytic from '@/scripts/libs/analytic';
import { Model } from '@viddsee/server-api-client';

import { PlayerTime } from './player-time';

import eventHandlers from './eventHandlers';
import {
  VideoPagePlayerOptions,
  VideoTrackerState,
  TrackerTrackEventName,
} from '@/scripts/models/VideoPagePlayerOptions';
import { assertPresenceOfPlayer } from './helpers';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
interface CallableOnOff {
  $on: CallableFunction;
  $off: CallableFunction;
}
const polyfillVueEmitter = (player: jwplayer.JWPlayer & Partial<CallableOnOff>): CallableOnOff => ({
  $on: (player.on || player.$on).bind(player),
  $off: (player.off || player.$off).bind(player),
});

/**
 * Tracker for video player
 */
class VideoTracker {
  seasonUuid: string;

  video: Model.Video.Info;

  playerOptions: VideoPagePlayerOptions;

  state!: VideoTrackerState;

  playerTimeTracker!: PlayerTime;

  createdAt: Date;

  player?: jwplayer.JWPlayer;

  emitter?: CallableOnOff;

  /**
   * @param {object} video - an video player object capable of handling player events with on / off
   * @param {PlayerOptions} playerOptions - information about the video needed to setup the tracker
   */
  constructor(video: Model.Video.Info, playerOptions: VideoPagePlayerOptions) {
    this.seasonUuid = uuidv1();
    this.video = video;
    this.playerOptions = playerOptions;
    this.initializeTrackerState();
    this.createdAt = new Date();
  }

  // Public methods
  setPlayer(player: jwplayer.JWPlayer): void {
    this.player = player;
    this.emitter = polyfillVueEmitter(player);
    this.setupEventListeners();

    this.state.sourceTime = new Date().getTime() - this.createdAt.getTime();
  }

  track(eventName: TrackerTrackEventName, ...params: any[]): void {
    if (!this.player) throw new Error('nothing to track. Please call setPlayer first');

    const tracker = this.createEventFunction(eventName);
    tracker && tracker(...params);
  }

  // Setup methods
  initializeTrackerState(): void {
    this.state = {
      isPinged: [],
      seeking: false,
      startTime: 0,
      playtime: 0,
      totalPlaytime: 0,
      sourceTime: 0,

      goneIntoFallback: false, // TODO: This need to be sync with the player. This is undesirable
      errorMessage: '',

      // Solely used for window close reporting
      bufferStart: null, // TODO: What is this? This is not in used meaningfully it seems..
      bufferDuration: 0,
      bufferCount: 0, // TODO: bufferCount is never changed
    };

    // NOTE: 2020Q1_REVAMP_NOTE PlayerTime has been revamped into a class. We do `new PlayerTime` instead of `PlayerTime.init`
    this.playerTimeTracker = new PlayerTime(
      this.video.id,
      this.playerOptions.player_type,
    );
  }

  setupEventListeners(): void {
    const emitter = this.emitter;
    if (!emitter) {
      throw new Error('emitter must be defined when this method is invoked');
    }

    // Automatically bind player's event
    Object
      .keys(eventHandlers.playerEventHandlers)
      .forEach((eventName) => {
        const fn = this.createEventFunction(eventName);
        if (!fn) return;
        emitter.$on(eventName, fn.bind(this));
      });

    // Automatically bind window's event
    Object
      .keys(eventHandlers.windowEvents)
      .forEach((eventName: string) => {
        const fn = this.createEventFunction(eventName);
        if (!fn) return;
        window.addEventListener('beforeunload', fn.bind(this) as any);
      });
  }

  removeEventListeners(): void {
    // TODO: Not implemented yet
  }

  createEventFunction(eventName: string): undefined | CallableFunction {
    const eventHandler = (eventHandlers.all as any)[eventName];
    if (typeof eventHandler !== 'function') {
      return;
    }

    return eventHandler.bind(null, this);
  }

  // Additional Tracker helpers methods
  updatePlaytime(): void {
    const player = assertPresenceOfPlayer(this.player);

    const timeStopped = player.getPosition();
    const timePlayed = (timeStopped - this.state.startTime);

    if (timePlayed > 0) {
      this.state.playtime += timePlayed;
      this.state.totalPlaytime += timePlayed;
    }
  }

  /**
   * TODO: I dont know what does this do.. why does it set playtime back to 0?
   * NOTE: 2020Q1_REVAMP_NOTE Update:
   *  - See: 45c114e7a8fb9592d49ab1b01098352407cbc0f3
   *  - https://github.com/viddsee/viddsee_api/blob/15863cf4a16f3d89ef2999804e2bfe812c807e8c/public/js/embed-player.js
   *
   * In embed tracker, action will be passed to the tracker. However, it is not passed to the tracker in video tracker.
   * This looks like a careless mistake? Should we pass action into the params too?
   *
   * @param {String} action - TODO: What is this?
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  reportSession(_action: string): void {
    const player = assertPresenceOfPlayer(this.player);

    this.sendToTracker('session', {
      start_time: this.state.startTime,
      play_time: this.state.playtime,
      film_duration: player.getDuration(),
    });

    this.state.playtime = 0;
    this.state.startTime = player.getPosition();
  }

  sendPlayerTimeToTracker(action: string, extras = {}): void {
    const player = assertPresenceOfPlayer(this.player);

    const trackerAction = `player_${action}`;
    // _log('_sendToTracker: ' + trackerAction);

    const isMobile = (
      (BrowserDetector.os() === 'AndroidOS') ||
      (BrowserDetector.os() === 'iOS' && BrowserDetector.md().phone() === 'iPhone')
    );

    const params = {
      last_position: player.getPosition(),
      // player_type: jwplayerObject.getProvider(),
      // product: options.video.player_type,
      video_id: this.video.id,
      // NOTE: 2020Q1_REVAMP_NOTE This was previously sending `this.video.country_code` which is always undefined
      country_code: this.playerOptions.country_code,
      platform: isMobile ? 'mobile' : 'desktop',
      source: this.playerOptions.service_type,

      // If extras exist, it will be append and replace existing property
      ...extras,
    };

    analytic.send({
      customProperties: {
        ga: false,
        snowplow: {
          selfDescribingEvent: {
            schema: 'iglu:com.viddsee/player_time/jsonschema/1-0-0',
            data: {
              video_id: this.video.id,
              last_position: params.last_position,
              total_time_play: (params as any).total_time_play,
              source: this.playerOptions.service_type,
              is_hosted_failover: false, // TODO: @CPWC REMOVE HARDCODING!
              is_player_error: false,
              is_player_pause: false,
            },
          },
        },
      },
    });

    webcall.tracker.postTrackerLog(
      trackerAction,
      params,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      () => {},
    );
  };

  sendToTracker(action: string, extras = {}): void {
    const player = assertPresenceOfPlayer(this.player);

    const trackerAction = `${this.playerOptions.player_type}_${action}`;
    // _log('_sendToTracker: ' + trackerAction);

    const isMobile = (
      (BrowserDetector.os() === 'AndroidOS') ||
      (BrowserDetector.os() === 'iOS' && BrowserDetector.md().phone() === 'iPhone')
    );

    const params = {
      last_position: player.getPosition(),
      // NOTE: 2020Q1_REVAMP_NOTE @types/jwplayer is missing getProvider function. The next line included a type shims to get getProvider back
      player_type: (player as (jwplayer.JWPlayer & { getProvider(): string })).getProvider(),
      product: this.playerOptions.player_type,
      video_id: this.video.id,
      // NOTE: 2020Q1_REVAMP_NOTE We has been sending this.video.country_code before this commit. It seems like we are losing country_code in the postTrackerLog
      country_code: this.playerOptions.country_code,
      platform: isMobile ? 'mobile' : 'desktop',
      source: this.playerOptions.service_type,

      // If extras exist, it will be append and replace existing property
      ...extras,
    };

    webcall.tracker.postTrackerLog(
      trackerAction,
      params,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      () => {},
    );
  };
};

export default VideoTracker;
